import { resolveBorderRadiusCandidatePx } from "../../shared/brand-border-radius.js";
import { isExtensionPopup } from "../../shared/extension-preview.js";
import { isThemeDefaultFontFamily, shouldOmitBlockFontFamily } from "../../shared/legacy-font-google.js";
import {
  formatBorderRadiusCss,
  resolvePopupThemeBorderRadius,
} from "./resolve-popup-theme-border-radius.js";

const DEFAULT_PRIMARY = "#0f172a";
const DEFAULT_PRIMARY_TEXT = "#ffffff";
const DEFAULT_TEXT = "#334155";
const DEFAULT_HEADING = "#0f172a";
const DEFAULT_MUTED = "#64748b";
const DEFAULT_BORDER = "#e2e8f0";
const DEFAULT_BACKGROUND = "#ffffff";
const DEFAULT_SECONDARY_TEXT = "#475569";

/** Shopify checkout / extension surface defaults — not store brand kit. */
const EXTENSION_SURFACE_TOKENS = Object.freeze({
  primary: "#1773b0",
  primaryText: "#ffffff",
  text: "#1f2937",
  heading: "#111827",
  muted: "#6b7280",
  secondary: "#6b7280",
  border: "#d1d5db",
  background: "#ffffff",
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  bodyFontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  borderRadiusPx: 4,
  buttonRadiusPx: 4,
});

/**
 * @param {string} hex
 * @returns {number} 0–100 lightness
 */
function hexLightness(hex) {
  const raw = String(hex || "").replace("#", "").trim();
  if (raw.length !== 6 && raw.length !== 3) return 50;
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return ((max + min) / 2) * 100;
}

/**
 * @param {string} hex
 * @returns {string}
 */
export function contrastButtonText(hex) {
  return hexLightness(hex) > 52 ? "#111827" : "#ffffff";
}

function pickColor(...candidates) {
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
}

function toCssFontStack(name) {
  if (shouldOmitBlockFontFamily(name)) return null;
  const cleaned = String(name || "").trim();
  if (!cleaned) return null;
  if (cleaned.includes(",")) return cleaned;
  return `"${cleaned}", system-ui, sans-serif`;
}

/**
 * Resolve popup theme tokens from generalSettings, brand theme, and shell customCSS.
 * Same sources legacy ButtonBlock / applyBrandThemeToPopup use.
 *
 * @param {object | null | undefined} popupData
 * @param {{ activeStep?: number, isMobile?: boolean }} [options]
 */
export function resolvePopupThemeTokens(popupData, options = {}) {
  if (isExtensionPopup(popupData)) {
    return { ...EXTENSION_SURFACE_TOKENS };
  }

  const activeStep =
    typeof options.activeStep === "number" && !Number.isNaN(options.activeStep)
      ? options.activeStep
      : 0;
  const isMobile = Boolean(options.isMobile);

  const gs = popupData?.generalSettings || {};
  const colors = gs.colors || {};
  const step = popupData?.schema?.[activeStep];
  const popupSettings = step?.popupSettings || {};
  const shellCss = popupSettings.customCSS || {};
  const shellSide = shellCss[isMobile ? "mobile" : "desktop"] || shellCss.desktop || {};

  const primary =
    pickColor(colors.primary, gs.primaryColor, shellSide.accentColor) ||
    DEFAULT_PRIMARY;
  const primaryText =
    pickColor(colors.primaryText, colors.buttonText) ||
    contrastButtonText(primary);
  const text =
    pickColor(colors.text, shellSide.color, colors.descriptionColor) ||
    DEFAULT_TEXT;
  const heading =
    pickColor(colors.titleColor, colors.subtitleColor, colors.heading) || text || DEFAULT_HEADING;
  const muted =
    pickColor(colors.mutedColor, colors.descriptionColor, colors.secondary) ||
    DEFAULT_MUTED;
  const border =
    pickColor(colors.border, shellSide.borderColor) || DEFAULT_BORDER;
  const background =
    pickColor(colors.background, shellSide.backgroundColor) || DEFAULT_BACKGROUND;
  const secondary = pickColor(colors.secondary) || muted;

  const fontRaw =
    pickColor(gs.fontFamily, gs.bodyFontFamily, shellSide.fontFamily) || "";
  const bodyFontRaw =
    pickColor(gs.bodyFontFamily, gs.fontFamily, shellSide.fontFamily) || fontRaw;
  const fontFamily = isThemeDefaultFontFamily(fontRaw)
    ? null
    : toCssFontStack(fontRaw) || '"Aimbrill-Poppins", system-ui, sans-serif';
  const bodyFontFamily = isThemeDefaultFontFamily(bodyFontRaw)
    ? fontFamily
    : toCssFontStack(bodyFontRaw) || fontFamily;

  const borderRadiusPx = resolvePopupThemeBorderRadius(popupData, {
    activeStep,
    isMobile,
  });
  const buttonRadiusPx =
    resolveBorderRadiusCandidatePx(gs.brandBorderRadius, "button") ??
    resolveBorderRadiusCandidatePx(gs.buttonRadiusPx, "button") ??
    Math.min(borderRadiusPx, 12);

  return {
    primary,
    primaryText,
    text,
    heading,
    muted,
    secondary,
    border,
    background,
    fontFamily,
    bodyFontFamily,
    shellUsesThemeFont: isThemeDefaultFontFamily(fontRaw),
    borderRadiusPx,
    buttonRadiusPx,
  };
}

/**
 * Inline style object with CSS custom properties for AI upsell + shared popup chrome.
 *
 * @param {ReturnType<typeof resolvePopupThemeTokens>} tokens
 */
export function buildPopupThemeCssVariables(tokens) {
  const radius = formatBorderRadiusCss(tokens.borderRadiusPx);
  const buttonRadius = formatBorderRadiusCss(tokens.buttonRadiusPx);

  return {
    "--we-primary": tokens.primary,
    "--we-primary-text": tokens.primaryText,
    "--we-text-primary": tokens.heading,
    "--we-text-secondary": tokens.text,
    "--we-text-muted": tokens.muted,
    "--we-border": tokens.border,
    "--we-background": tokens.background,
    "--we-radius": radius,
    "--we-button-radius": buttonRadius,
    "--ai-upsell-primary": tokens.primary,
    "--ai-upsell-primary-text": tokens.primaryText,
    "--ai-upsell-text": tokens.text,
    "--ai-upsell-heading": tokens.heading,
    "--ai-upsell-muted": tokens.muted,
    "--ai-upsell-border": tokens.border,
    "--ai-upsell-background": tokens.background,
    ...(tokens.fontFamily ? { "--ai-upsell-font": tokens.fontFamily } : {}),
    ...(tokens.fontFamily ? { "--pr-font": tokens.fontFamily } : {}),
    "--ai-upsell-border-radius": radius,
    "--ai-upsell-button-radius": buttonRadius,
    "--ai-upsell-primary-soft": `color-mix(in srgb, ${tokens.primary} 12%, white)`,
    "--ai-upsell-primary-border": `color-mix(in srgb, ${tokens.primary} 28%, white)`,
    "--ai-upsell-primary-label": `color-mix(in srgb, ${tokens.primary} 72%, #1e1b4b)`,
    "--ai-upsell-ribbon-bg": `color-mix(in srgb, ${tokens.primary} 8%, white)`,
    "--ai-upsell-ribbon-border": `color-mix(in srgb, ${tokens.primary} 18%, white)`,
  };
}

/**
 * CTA styles for catalog layout — respects existing section customCSS (theme or merchant).
 *
 * @param {object | null | undefined} section
 * @param {ReturnType<typeof resolvePopupThemeTokens>} tokens
 * @param {{ primary?: boolean }} [options]
 */
export function resolveCatalogCtaCssPatch(section, tokens, options = {}) {
  const { primary = true } = options;
  const customCSS = section?.setting?.customCSS || {};
  const desktop = customCSS.desktop || {};
  const mobile = customCSS.mobile || {};

  if (primary) {
    return {
      desktop: {
        backgroundColor: desktop.backgroundColor ?? tokens.primary,
        color: desktop.color ?? tokens.primaryText,
        borderColor: desktop.borderColor ?? tokens.primary,
      },
      mobile: {
        backgroundColor: mobile.backgroundColor ?? desktop.backgroundColor ?? tokens.primary,
        color: mobile.color ?? desktop.color ?? tokens.primaryText,
        borderColor: mobile.borderColor ?? desktop.borderColor ?? tokens.primary,
      },
    };
  }

  return {
    desktop: {
      backgroundColor: desktop.backgroundColor ?? tokens.background,
      color: desktop.color ?? tokens.muted,
      borderColor: desktop.borderColor ?? tokens.border,
    },
    mobile: {
      backgroundColor: mobile.backgroundColor ?? desktop.backgroundColor ?? tokens.background,
      color: mobile.color ?? desktop.color ?? tokens.muted,
      borderColor: mobile.borderColor ?? desktop.borderColor ?? tokens.border,
    },
  };
}
